import { useState, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Save, Loader2 } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';

interface NotaRapidaProps {
  clienteId?: string;
  pedidoId?: string;
  triagemId?: string;
  leadId?: string;
  cnpj?: string;
  onSuccess?: () => void;
  onNotaText?: (texto: string) => void;
}

export function NotaRapida({ clienteId, pedidoId, triagemId, leadId, cnpj, onSuccess, onNotaText }: NotaRapidaProps) {
  const [nota, setNota] = useState('');
  const lastTextoRef = useRef('');
  const salvandoRef = useRef(false);
  const { toast } = useToast();
  const { profile } = useAuth();
  const queryClient = useQueryClient();

  const salvarNotaMutation = useMutation({
    mutationFn: async (texto: string) => {
      lastTextoRef.current = texto;
      if (!texto.trim()) {
        throw new Error('Digite uma nota antes de salvar');
      }
      if (!profile?.id) throw new Error('Aguarde a identificação do usuário antes de salvar.');

      // Se tem triagemId, salvar como movimento de triagem
      if (triagemId) {
        if (!cnpj) throw new Error('Sem CNPJ vinculado à triagem');

        const { error } = await (supabase as any).from('movimento').insert({
          entidade: 'triagem',
          entidade_id: triagemId,
          tipo: 'triagem_nota',
          descricao: texto.trim(),
          cnpj,
          usuario_id: profile?.id,
        });
        if (error) throw error;
        return;
      }

      // Se tem leadId, salvar como observação manual no histórico do lead
      if (leadId) {
        const { error } = await supabase.from('leads_carteira_historico').insert({
          lead_id: leadId,
          acao: 'OBSERVACAO_MANUAL',
          observacao: texto.trim(),
          dados: { observacao: texto.trim() },
          usuario_id: profile?.id,
        });
        if (error) throw error;
        return;
      }

      // Fluxo original: salvar como mensagem interna
      if (!clienteId && !pedidoId) {
        throw new Error('Nenhum contexto fornecido (cliente ou pedido)');
      }

      let finalClienteId = clienteId;
      
      if (!finalClienteId && pedidoId) {
        const { data: pedido, error: pedidoError } = await supabase
          .from('pedidos')
          .select('cliente_id')
          .eq('id', pedidoId)
          .single();
        
        if (pedidoError) throw new Error('Erro ao buscar dados do pedido');
        if (!pedido?.cliente_id) throw new Error('Pedido sem cliente associado');
        
        finalClienteId = pedido.cliente_id;
      }

      const { error } = await supabase
        .from('mensagem')
        .insert({
          cliente_id: finalClienteId,
          pedido_id: pedidoId || null,
          canal: 'interno',
          direcao: 'out',
          corpo: texto.trim(),
          criado_por: profile?.id,
          status: 'enviado'
        });

      if (error) throw error;
    },
    onSuccess: () => {
      toast({
        title: 'Nota salva',
        description: triagemId ? 'Nota da triagem adicionada à timeline' : 'A nota foi adicionada à timeline',
        duration: 2000
      });
      
      onNotaText?.(lastTextoRef.current);
      setNota('');
      onSuccess?.();
      
      // Invalidar queries relevantes
      if (cnpj) {
        queryClient.invalidateQueries({ queryKey: ['timeline-unificada', cnpj] });
      } else if (leadId) {
        // lead CPF: a linha do tempo dele é pelo número do lead, sem CNPJ na chave
        queryClient.invalidateQueries({ queryKey: ['timeline-unificada'] });
      }
      if (clienteId) {
        queryClient.invalidateQueries({ queryKey: ['timeline-cliente', clienteId] });
      }
      if (pedidoId) {
        queryClient.invalidateQueries({ queryKey: ['timeline-pedido-modal', pedidoId] });
        queryClient.invalidateQueries({ queryKey: ['pedido-activity-count', pedidoId] });
      }
    },
    onSettled: () => { salvandoRef.current = false; },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao salvar nota',
        description: error.message,
        variant: 'destructive'
      });
    }
  });

  const handleSalvar = () => {
    if (salvandoRef.current || !nota.trim() || !profile?.id) return;
    salvandoRef.current = true;
    salvarNotaMutation.mutate(nota);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      e.preventDefault();
      handleSalvar();
    }
  };

  return (
    <Card className="relative overflow-hidden pl-1">
      <span aria-hidden className="absolute inset-y-0 left-0 w-1 bg-[linear-gradient(180deg,#6E1626,#4E1522_55%,#3E0E1B)]" />
      <CardContent className="p-3">
        <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.08em] text-foreground">Nota rápida</p>
        <Textarea
          placeholder={
            triagemId ? 'O que aconteceu nesta triagem…'
              : leadId ? 'O que aconteceu neste lead…'
              : clienteId ? 'O que aconteceu com este cliente…'
              : 'O que aconteceu…'
          }
          value={nota}
          onChange={(e) => setNota(e.target.value)}
          onKeyDown={handleKeyDown}
          className="min-h-[64px] resize-none text-xs"
        />
        <div className="mt-2 flex items-center justify-between gap-2">
          <p className="whitespace-nowrap text-[10px] text-muted-foreground">
            <kbd className="rounded bg-muted px-1 py-0.5 text-[9px]">Ctrl+Enter</kbd> salva
          </p>
          <Button
            onClick={handleSalvar}
            disabled={salvarNotaMutation.isPending || !nota.trim() || !profile?.id}
            className="h-[30px] shrink-0 gap-1.5 border-0 bg-[linear-gradient(90deg,#4A1320,#5B0A0D)] px-3 text-xs font-bold text-white shadow-[0_2px_6px_rgba(91,10,13,0.30)] transition-all hover:brightness-110 dark:bg-[linear-gradient(90deg,#9D2530,#6C2236)]"
          >
            {salvarNotaMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            Salvar nota
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
